import * as React from "react";
import { NumberTextBoxEditor } from "../../../../../../../react-core/Html/NumberTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { addYears } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { ClientDetails } from "../../../../../../../Wrap/Models/Models/Processes/Quote/ClientDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { IJsonDate } from "../../../../../../../Wrap/System/IJsonDate";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { DrawdownCalculationAction } from "./Action/DrawdownCalculationAction";
import { calculateBeneiftsCharge, calculateBeneiftsNet, hasPrimaryProtection } from "./Helper/DrawdownDetailHelper";
import { PensionOptionMap } from "./Resource/Contents";

interface IPensionOptionsProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: PensionProtectionDetails;
    clientDetail: ClientDetails;
    validateDrawdown: (data: DrawdownModel) => void;
}

interface IPensionOptionsState {
    dri: number;
    itfc: number;
    driAlert: boolean;
    itfcAlert: boolean;
}

export class PensionOptions extends ProcessComponent<IPensionOptionsProps, IPensionOptionsState> {
    constructor(props: any) {
        super(props);
        this.state = {
            driAlert: false,
            itfcAlert: false,
            dri: null,
            itfc: null,
        };
    }

    private olderThan(age: number, dateOfBirth: IJsonDate) {
        const now = new Date();
        const birth = JsonDateParser.toDate(dateOfBirth);
        return addYears(birth, age) <= now;
    }

    private hasUFPLS = (): boolean => {
        const { pensionProtection, clientDetail, data } = this.props;

        if (hasPrimaryProtection(pensionProtection)) {return false;}
        if (
            pensionProtection?.PensionEnhancedProtectionDetails &&
            pensionProtection?.PensionEnhancedProtectionDetails?.PclsEntitlement
        ) {
            return false;
        }
        if (
            this.olderThan(77, clientDetail?.DateOfBirth) &&
            data?.LifetimeAllowance?.AvailablePersonalAllowance?.Amount <= 0
        ) {
            return false;
        }
        return true;
    };

    private hasDrawdown = (): boolean => {
        return true;
    };

    private validateId = (id, isITFC) => {
        if (!id) {return;}
        DrawdownCalculationAction.validateRequestId(id, this.props.data, isITFC, (d) => {
            if (d.length > 0) {
                if (isITFC) {
                    this.setState({ itfcAlert: true });
                } else {
                    this.setState({ driAlert: true });
                }
            } else {
                const _data: DrawdownModel = JSON.parse(JSON.stringify(d));

                if (_data?.CrystallisationDetails) {
                    _data.CrystallisationDetails.AvailableToCrystallise = {
                        Amount: 100000,
                        Currency: CurrencyCode.GBP,
                    };
                    _data.CrystallisationDetails.CrystallisedValue = {
                        Amount: 100000,
                        Currency: CurrencyCode.GBP,
                    };
                    _data.CrystallisationDetails.UnCrystallisedValue = {
                        Amount: 100000,
                        Currency: CurrencyCode.GBP,
                    };
                    _data.CrystallisationDetails.AvailableCash = {
                        Amount: 10000,
                        Currency: CurrencyCode.GBP,
                    };
                }

                _data.CurrentPclsEntitlement = {
                    Amount: 13413.75,
                    Currency: CurrencyCode.GBP,
                };

                _data.DrawdownPriceLockPercentage = 0.35;
                _data.DrawdownPriceLock = false;
                _data.LifetimeAllowance = {
                    ChargeableCrystallisationPortion: {
                        Amount: 46345,
                        Currency: CurrencyCode.GBP,
                    },
                    AvailablePersonalAllowance: {
                        Amount: 53655,
                        Currency: CurrencyCode.GBP,
                    },
                    SlaBenefitsPriorTo2006: 5,
                    TotalSlaUsed: 104.31,
                    SlaPreviouslyCrystallised: 90,
                    SlaUsedByProposedCrystallisation: 9.31,
                };
                this.props.validateDrawdown(_data);
                // this.props.validateDrawdown(d);
            }
        });
    };

    public render(): JSX.Element {
        const { data, onChange } = this.props;

        return (
            <>
                {SectionHeader("Pension options")}
                <RadiosEditor
                    options={[PensionOption.Drawdown, PensionOption.UFPLS]}
                    getText={(v) => PensionOptionMap[v]}
                    validationRules={[
                        {
                            errorMessage: "Can not select this type",
                            validator: (v) => {
                                if (v == PensionOption.UFPLS) {return this.hasUFPLS();}
                                if (v == PensionOption.Drawdown) {return this.hasDrawdown();}
                                return true;
                            },
                        },
                    ]}
                    onChange={(v) =>
                        onChange({
                            PensionOption: v,
                            GrossExcessBenefitLumpSum: v == PensionOption.UFPLS ? 100 : 0,
                            GrossExcessBenefitTotal: v == PensionOption.UFPLS ? 100 : 0,
                            ExcessBenefitsCalculations: {
                                ...data?.ExcessBenefitsCalculations,
                                LumpSumTaxPayable: {
                                    Amount:
                                        v == PensionOption.UFPLS ? calculateBeneiftsCharge(data, 100, "lumpsum") : 0,
                                    Currency: CurrencyCode.GBP,
                                },
                                LumpSumTotalNetBenefits: {
                                    Amount: v == PensionOption.UFPLS ? calculateBeneiftsNet(data, 100, "lumpsum") : 0,
                                    Currency: CurrencyCode.GBP,
                                },
                            },
                        })
                    }
                    required="Please select Pension options"
                    selected={data?.PensionOption}
                    cssWrapper="rg-inline mb-10"
                />
                <Alert
                    alertType={AlertType.Warning}
                    message={
                        <>
                            <p>
                                If there is insufficient cash to fund the tax-free lump sum, we will sell assets to
                                cover the payment, in line with any deducation preference.
                            </p>
                            <p>
                                Any phasing instructions will cease following a crystallisation. Please contact us after
                                submitting the application if you would like phasing to continue.
                            </p>
                            <p>
                                For more information about our crystallisation process, please contact our support team
                                at xxxxxxxxx.
                            </p>
                        </>
                    }
                />
                {data?.PensionOption != PensionOption.UFPLS && (
                    <>
                        <div className="row">
                            <div className="col-4">
                                <NumberTextBoxEditor
                                    label="Request ID(optional)"
                                    validateOnTyping={false}
                                    // regex={{ value: Numeric7, errorMessage: "Invalid format, please re-enter" }}
                                    onChange={(v) => {
                                        this.setState({ driAlert: false, itfcAlert: false, itfc: null, dri: v });
                                    }}
                                    // maxLength={7}
                                    value={this.state.dri}
                                    cssWrapper="control-size-245"
                                />
                            </div>
                            <div className="col-4">
                                <button
                                    className="btn secondary mr-10 mt-30"
                                    onClick={() => this.validateId(this.state.dri, false)}
                                >
                                    Validate
                                </button>
                            </div>
                        </div>
                        {this.state.driAlert && (
                            <Alert alertType={AlertType.Error} message={"drawdown request id is not valid"} />
                        )}
                    </>
                )}
                {data?.PensionOption != PensionOption.UFPLS && (
                    <>
                        <div className="row">
                            <div className="col-4">
                                <NumberTextBoxEditor
                                    label="ITFC Request ID (optional)"
                                    validateOnTyping={false}
                                    // regex={{ value: Numeric7, errorMessage: "Invalid format, please re-enter" }}
                                    onChange={(v) => {
                                        this.setState({ itfcAlert: false, driAlert: false, dri: null, itfc: v });
                                    }}
                                    // maxLength={7}
                                    value={this.state.itfc}
                                    cssWrapper="control-size-245"
                                />
                            </div>
                            <div className="col-4">
                                <button
                                    className="btn secondary mr-10 mt-30"
                                    onClick={() => this.validateId(this.state.itfc, true)}
                                >
                                    Validate
                                </button>
                            </div>
                        </div>
                        {this.state.itfcAlert && (
                            <Alert alertType={AlertType.Error} message={"ITFC request id is not valid"} />
                        )}
                    </>
                )}
            </>
        );
    }
}
